/**
 * Preserves the deep-linked /portal-view/... URL a staff user opened across
 * the Entra interactive sign-in redirect.
 *
 * The staff app's registered Entra redirect URI is the staff app root
 * (apps/frontend), not /portal-view/... (apps/customer-portal's staff-view
 * build, served at that path by nginx — see apps/customer-portal/vite.config.ts
 * and apps/customer-portal/src/staff-view/routes.tsx's "/portal-view" basename).
 * A fresh tab opening a /portal-view/... link with no cached MSAL session goes
 * through loginRedirect() and lands back at the staff app root, losing the
 * deep link — unless the original path rides along as MSAL's `state`
 * parameter and the staff app's redirect handler (apps/frontend/src/main.tsx)
 * forwards it back.
 *
 * Both the encode side (apps/customer-portal/src/staff-view/staffAuth.ts,
 * before loginRedirect) and the decode side (apps/frontend/src/main.tsx,
 * after handleRedirectPromise) validate against the same same-origin guard,
 * so a `state` value this app did not itself mint — it crosses a network hop
 * to Entra and back — can never be used as an open-redirect vector.
 */

// The customer portal's staff view and the supplier portal's: each is a bundle the staff image serves under its own
// prefix (docs/features/supplier-portal-view-as/05 §6.6).
const STAFF_VIEW_PREFIXES = [
  "/portal-view/",
  "/supplier-portal-view/",
] as const;

/**
 * True when `path` is a same-origin path under /portal-view/ or /supplier-portal-view/ that is safe to
 * navigate to directly (window.location.replace/assign). Rejects anything
 * that could be interpreted as a different origin — an absolute URL with a
 * scheme (`https://evil.com`), a protocol-relative URL (`//evil.com`), or any
 * path that does not start with the exact /portal-view/ prefix.
 */
export function isPortalViewReturnPath(path: string): boolean {
  if (typeof path !== "string" || path.length === 0) return false;
  if (!STAFF_VIEW_PREFIXES.some((prefix) => path.startsWith(prefix)))
    return false;
  // The prefix test above already rules out a path beginning with a second
  // leading slash or backslash (either of which the browser would resolve as
  // a protocol-relative URL to a different origin), but guard explicitly so
  // this invariant does not silently depend on the exact prefixes chosen above.
  if (path.startsWith("//") || path.startsWith("/\\")) return false;
  return true;
}

/**
 * Captures the current staff-view location as MSAL `state` before an
 * interactive redirect. Returns null — never a value to pass as `state` —
 * for anything that is not a valid staff-view path, so a caller can never
 * accidentally carry an unvalidated value into the redirect.
 */
export function encodePortalViewReturnPath(
  pathname: string,
  search: string,
): string | null {
  const path = `${pathname}${search}`;
  return isPortalViewReturnPath(path) ? path : null;
}

/**
 * Reads MSAL's returned `state` back out after the redirect completes.
 * Re-validates independently of the encode side — `state` is untrusted input
 * here, not a value this app can assume it minted unmodified.
 */
export function decodePortalViewReturnPath(
  state: string | null | undefined,
): string | null {
  if (!state) return null;
  return isPortalViewReturnPath(state) ? state : null;
}
